import * as React from 'react';
import { jsx, css } from '@emotion/react';
import { mediaQueries, autoResponsiveClamp } from 'Src/me/pages/guest/proposals/new/React/viewHelpers';
import { MOBILE_MAX_WIDTH } from 'Src/me/pages/guest/proposals/new/consts';
import type { BaseAnswerType } from '../../../../../../../chat/views/types';
import type { BuildingTypeButtonGroupPropsType } from '../..';
import { simpleButtonActiveStyle, simpleButtonAnsweredStyle, simpleButtonHoverStyle, simpleButtonStyle } from '../../../SimpleButtonGroup/components/SimpleButton';

import HousePcPngPath from 'Images/me/pages/guest/proposals/new/house_pc.png'
import HousePcWebPPath from 'Images/me/pages/guest/proposals/new/house_pc.webp'
import HouseMobilePngPath from 'Images/me/pages/guest/proposals/new/house_mobile.png'
import HouseMobileWebPPath from 'Images/me/pages/guest/proposals/new/house_mobile.webp'

const { tablet, mobile } = mediaQueries();

type BuildingTypeAnswerButtonPropsType = Merge<BuildingTypeButtonGroupPropsType & Required<BaseAnswerType> & {
  isAnswered: boolean;
  onClickHandler: (event: React.MouseEvent<HTMLButtonElement>) => void;
}>;

export const BuildingTypeAnswerButton = ({
  id,
  value,
  textContents,
  gaEventName,
  isAnswered,
  onClickHandler: onClickHandlerFromProps,
  answers,
}: BuildingTypeAnswerButtonPropsType) => {
  const [isActive, setIsActive] = React.useState<boolean>(null);
  const onClickHandler = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const { value: currentValue } = event.currentTarget;

    if (currentValue === '0') {
      event.currentTarget.disabled = true;
    } else {
      // ボタンをアクティブに変更
      setIsActive(true);
    }

    // ボタンのクリックイベントを実行
    onClickHandlerFromProps(event);
  }, [onClickHandlerFromProps]);

  return (
    <div
      css={css`
        padding: 0 0 4px;
        position: relative;
      `}
    >
      <button
        type="button"
        id={`ga_event_${gaEventName}_${id}`}
        value={value}
        onClick={onClickHandler}
        css={css`
          ${simpleButtonStyle}

          ${value === '0' && css`
            font-size: 20px;

            ${mobile(({ clamp }) => css`
              font-size: ${clamp('13px', '18px')};
            `)}
          `}

          ${value === '1' && css`
            font-size: 32px;
            gap: 0 10px;

            ${mobile(({ clamp }) => css`
              font-size: ${clamp('20px', '24px')};
              gap: 0 ${clamp('10px', '30px')};
            `)}
          `}

          &:disabled {
            color: #b7b7b7;
            pointer-events: none;
          }

          &:hover {
            ${simpleButtonHoverStyle}
          }

          ${isActive !== null && isActive && css`
            ${simpleButtonActiveStyle}
          `}

          ${isAnswered && css`
            ${simpleButtonAnsweredStyle}
          `}
        `}
      >
        {value === '1' && (
          <span
            css={css`
              font-size: 0;
              width: 100%;
              max-width: 68px;

              ${mobile(({ clamp }) => css`
                max-width: ${clamp('44px', '60px')};
              `)}

              img {
                width: 100%;
                height: auto;
              }
            `}
          >
            <picture>
              <source
                srcSet={HouseMobileWebPPath}
                media={`(max-width: ${MOBILE_MAX_WIDTH}px)`}
                type="image/webp"
              />
              <source
                srcSet={HouseMobilePngPath}
                media={`(max-width: ${MOBILE_MAX_WIDTH}px)`}
              />
              <source
                srcSet={HousePcWebPPath}
                type="image/webp"
              />
              <img
                src={HousePcPngPath}
                alt="一戸建て"
              />
            </picture>
          </span>
        )}
        {textContents}
      </button>
    </div>
  );
};
